import { Alert, AlertIcon, Box, ListItem, Stack, Text, UnorderedList } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiAward } from "react-icons/fi";
import type { EventValueDto, PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";
import { prizeLabel } from "../../buyer/wizard/utils/designContest";
import { SectionCard } from "./SectionCard";

export interface DesignContestTerms {
  juryCriteria: string[];
  prizes: { description?: string; value?: EventValueDto | null }[];
  jury: { name?: string; role?: string | null }[];
  requiredParticipantQualification?: string | null;
  leadsToServiceContract?: boolean | null;
}

export const DesignContestAnonymityNotice = ({ children }: { children?: ReactNode }) => {
  const { t } = useTranslation();
  return (
    <Alert status="info" borderRadius="md">
      <AlertIcon />
      <Box>
        <Text fontSize="sm">
          {t(
            "The jury judges the entries anonymously. Do not put your name, logo or anything else that identifies you in the entry itself.",
          )}
        </Text>
        {children}
      </Box>
    </Alert>
  );
};

export const EventDesignContestSection = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  if (!event.designContest) return null;
  return (
    <DesignContestTermsSection
      contest={event.designContest}
      notice={t(
        "The jury judges the entries anonymously. Do not put your name, logo or anything else that identifies you in the entry itself.",
      )}
    />
  );
};

export const DesignContestTermsSection = ({
  contest,
  notice,
  memberDetail,
}: {
  contest: DesignContestTerms;
  notice: string;
  memberDetail?: (index: number) => ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <SectionCard icon={FiAward} title={t("The design contest")}>
      <Stack spacing="5">
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">{notice}</Text>
        </Alert>
        <Box>
          <Text fontWeight="semibold" fontSize="sm">
            {t("The jury's evaluation criteria")}
          </Text>
          <UnorderedList fontSize="sm" mt="1">
            {contest.juryCriteria.map((criterion) => (
              <ListItem key={criterion}>{criterion}</ListItem>
            ))}
          </UnorderedList>
        </Box>
        {contest.prizes.length > 0 && (
          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              {t("Prizes and payments")}
            </Text>
            <UnorderedList fontSize="sm" mt="1">
              {contest.prizes.map((prize, index) => (
                <ListItem key={`${prize.description}-${index}`}>{prizeLabel(prize)}</ListItem>
              ))}
            </UnorderedList>
          </Box>
        )}
        {contest.jury.length > 0 && (
          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              {t("Jury members")}
            </Text>
            <UnorderedList fontSize="sm" mt="1">
              {contest.jury.map((member, index) => (
                <ListItem key={`${member.name}-${index}`}>
                  {[member.name, member.role].filter(Boolean).join(", ")}
                  {memberDetail?.(index)}
                </ListItem>
              ))}
            </UnorderedList>
          </Box>
        )}
        {contest.requiredParticipantQualification && (
          <Box>
            <Text fontWeight="semibold" fontSize="sm">
              {t("Professional qualification required of the participants")}
            </Text>
            <Text fontSize="sm" mt="1">
              {contest.requiredParticipantQualification}
            </Text>
          </Box>
        )}
        {contest.leadsToServiceContract != null && (
          <Text fontSize="sm">
            {contest.leadsToServiceContract
              ? t("A service contract follows the contest, negotiated with the winner or winners.")
              : t("No contract follows the contest. The winners receive the prizes.")}
          </Text>
        )}
      </Stack>
    </SectionCard>
  );
};
